import { useState } from "react";
import { Link } from "react-router-dom";

import { Loading } from "../components/StateViews";
import { TermSearch } from "../components/TermSearch";
import { trpc } from "../trpc";

const LETTERS = "abcdefghijklmnopqrstuvwxyz".split("");

export function GlossaryPage() {
  const [letter, setLetter] = useState<string | null>(null);
  const list = trpc.glossary.list.useInfiniteQuery(
    { letter: letter ?? undefined, limit: 60 },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );

  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mx-auto max-w-6xl px-5 pt-14 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">The glossary</p>
          <h1 className="mt-3 font-serif text-4xl text-ink">
            Every word of the text
          </h1>
        </div>
        <Link
          to="/drill"
          className="rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-paper transition-opacity hover:opacity-90"
        >
          Drill the words
        </Link>
      </div>

      <div className="mt-8 max-w-2xl">
        <TermSearch />
      </div>

      <div className="mt-10 flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setLetter(null)}
          className={letterClass(letter === null)}
        >
          All
        </button>
        {LETTERS.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLetter(l)}
            className={letterClass(letter === l)}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      {list.isLoading ? (
        <Loading label="Gathering words" />
      ) : (
        <>
          <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((term) => (
              <li key={term.slug}>
                <Link
                  to={`/glossary/${term.slug}`}
                  className="flex items-baseline justify-between gap-2 border-b border-line/60 py-2.5 transition-colors hover:text-terracotta"
                >
                  <span className="font-serif text-lg text-ink">
                    {term.displayName}
                  </span>
                  {term.isStub ? (
                    <span className="eyebrow text-ink-faint/60">·</span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>

          {list.hasNextPage ? (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => void list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
                className="rounded-full border border-line-strong px-6 py-2 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk disabled:opacity-50"
              >
                {list.isFetchingNextPage ? "Loading…" : "More words"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function letterClass(active: boolean): string {
  return [
    "flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors",
    active
      ? "bg-ink text-paper"
      : "text-ink-faint hover:bg-paper-sunk hover:text-ink",
  ].join(" ");
}
